import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { SteamSignInButton } from "@/components/SteamSignInButton";
import { api, type Me } from "@/lib/api";
import { ArrowRight, Plus } from "lucide-react";

interface LandingProps {
  me: Me | null;
  onMeChange: (me: Me | null) => void;
}

export function Landing({ me, onMeChange }: LandingProps) {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    if (params.get("error") === "auth") {
      toast.error("Steam sign-in failed");
      params.delete("error");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const needsSync = useMemo(() => me && !me.lastLibrarySyncAt, [me]);

  async function handleCreate() {
    setCreating(true);
    try {
      const { code } = await api.createRoom();
      navigate(`/r/${code}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not create room");
    } finally {
      setCreating(false);
    }
  }

  async function handleJoin(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    setJoining(true);
    try {
      await api.joinRoom(trimmed);
      navigate(`/r/${trimmed}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not join room");
    } finally {
      setJoining(false);
    }
  }

  async function handleSync() {
    const t = toast.loading("Syncing your Steam library...");
    try {
      const result = await api.syncLibrary();
      toast.success(`Synced ${result.count} games`, { id: t });
      onMeChange(await api.me());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Sync failed", { id: t });
    }
  }

  return (
    <main className="container flex flex-1 items-center justify-center py-10">
      <div className="grid w-full max-w-5xl gap-8 lg:grid-cols-2">
        <section className="flex flex-col justify-center gap-6">
          <div>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              Can't decide what to play?
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Sign in with Steam, gather your friends in a room, and let the wheel pick a
              game everyone owns.
            </p>
          </div>

          {!me ? (
            <div>
              <SteamSignInButton size="lg" />
            </div>
          ) : needsSync ? (
            <Card>
              <CardHeader>
                <CardTitle>One more step</CardTitle>
                <CardDescription>
                  Sync your Steam library so the wheel knows what you own. You only need to
                  do this once (and again whenever you buy new games).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button size="lg" onClick={handleSync}>
                  Sync my library
                </Button>
              </CardContent>
            </Card>
          ) : null}
        </section>

        <section className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Start a room</CardTitle>
              <CardDescription>
                Create a room and share the link with your friends. Each friend signs in
                with Steam.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                size="lg"
                className="w-full"
                disabled={!me || creating}
                onClick={handleCreate}
              >
                <Plus />
                {creating ? "Creating..." : "Create new room"}
              </Button>
              {!me && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Sign in with Steam first.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Join a room</CardTitle>
              <CardDescription>Type or paste the 6-character room code.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleJoin} className="flex gap-2">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="ABC123"
                  maxLength={6}
                  className="font-mono uppercase tracking-widest"
                  autoCapitalize="characters"
                />
                <Button type="submit" disabled={!me || joining || code.trim().length === 0}>
                  Join
                  <ArrowRight />
                </Button>
              </form>
            </CardContent>
          </Card>
        </section>
      </div>
    </main>
  );
}
